import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { LucideX, Download, Eye, FileText, XIcon } from "lucide-react";
import Modal from "../../../../components/modal/Modal";
import { Dropdown } from "../../../../components/dropdown/Dropdown";
import { useS3Upload, S3TempUrl } from "../../../../utils/s3Client";
import DocumentViewer from "../../../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../../../components/pdf/PdfViewer";
import {
  useEquipmentManagementStore,
  useFireSafetyAudits,
  useOrgUsersStore,
  useRoleManageStore,
} from "../../../../store/store";
import FileUpload from "../../../../components/FileUpload/FileUpload";
import { EquipmentInventory, MaintenanceTask } from "../../../../types/api";
import {
  useEquipmentManagementStore as useEquipmentManagementMasterStore,
  useEquipmentSubcategoryStore,
  useMaintenanceTypeListStore,
} from "../../../../store/masterDataStore";
import { fetchAllById } from "../../../../service/api";
import { useAuth } from "../../../../auth/AuthProvider";
import { useDropzone } from "react-dropzone";
import { useLocation } from "react-router-dom";

// Utility functions for file handling
const getFileExtension = (filename: string): string => {
  return filename.split(".").pop()?.toLowerCase() || "";
};

const canPreviewFile = (filename: string): boolean => {
  const extension = getFileExtension(filename);
  return ["pdf", "jpg", "jpeg", "png", "doc", "docx"].includes(extension);
};

interface FilePreviewState {
  url: string;
  name: string;
  type: string;
}
interface Attachment {
  url: string;
  filename?: string;
  name?: string;
  originalName?: string;
  uploadedAt?: string;
}
// Assignment types
type AssignmentType = "maintenance_team" | "vendor" | "internal_staff";

// Maintenance Task Modal Component
interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: MaintenanceTask | null;
  mode: "create" | "view" | "update" | "complete";
  onTaskComplete: () => void;
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  isOpen,
  onClose,
  task,
  mode,
  onTaskComplete,
}) => {
  const [formData, setFormData] = useState<Partial<MaintenanceTask>>({
    taskId: "",
    equipmentName: "",
    maintenanceType: "",
    scheduledDate: "",
    assignedTo: "",
    completionDate: "",
    checkList: null,
    status: "Scheduled",
    remarks: "",
    frequency: "",
    isVendorTask: false,
  });
  const location = useLocation();
  const getModuleName = (): string | undefined => {
    const path = location.pathname;
    if (path.includes("/hazardous-materials")) {
      return "hazardous_materials";
    } else if (
      path.includes("/equipment-management/maintenance-scheduling-and-tracking")
    ) {
      return "equipment_management";
    } else if (
      path.includes("/fire-system") ||
      path.startsWith("/fire-safety-audits/create-check-list")
    ) {
      return "fire_system";
    }
    return undefined;
  };
  const myModuleName = getModuleName() || "";
  // Dynamic form states
  const [assignmentType, setAssignmentType] =
    useState<AssignmentType>("maintenance_team");
  const [showSchedulingDetails, setShowSchedulingDetails] = useState(false);
  const [showCompletionFields, setShowCompletionFields] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [selectedEquipmentTypeId, setSelectedEquipmentTypeId] =
    useState<string>("");
  const [selectedEquipmentSubcategoryId, setSelectedEquipmentSubcategoryId] =
    useState<string>("");
  const [equipmentOptions, setEquipmentOptions] = useState<
    EquipmentInventory[]
  >([]);
  const [equipmentOptionsLoading, setEquipmentOptionsLoading] = useState(false);
  const [maxFileSize, setMaxFileSize] = useState<number>(10);
  const [files, setFiles] = useState<File[] | null>(null);

  const [uploadErrorMessage, setUploadErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { allChickList, getAllChecklist } = useFireSafetyAudits();

  // Document preview states
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    [],
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);

  const { user } = useAuth();
  const userRole = user?.roles?.[0];
  const [fileurl, setFileUrl] = useState<FilePreviewState>({
    url: "",
    name: "",
    type: "",
  });
  useEffect(() => {
    if (myModuleName) {
      getAllChecklist(myModuleName);
    }
  }, [myModuleName, getAllChecklist]);
  const { getsignedurl } = S3TempUrl();
  const handleFileChange = async (acceptedFiles: File[]) => {
    if (mode === "view") return;

    const maxTotalSize = maxFileSize * 1024 * 1024;
    const existingFiles = files || [];
    const existingTotalSize = existingFiles.reduce(
      (sum, file) => sum + file.size,
      0,
    );

    const newFilesTotalSize = acceptedFiles.reduce(
      (sum, file) => sum + file.size,
      0,
    );
    const totalSize = existingTotalSize + newFilesTotalSize;

    if (totalSize > maxTotalSize) {
      toast.error(`Total file size exceeds the ${maxFileSize}MB limit.`);
      return;
    }

    const updatedFiles = [...existingFiles, ...acceptedFiles];
    handleFilesChange(updatedFiles);
  };
  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileChange,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "text/plain": [".txt"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    // Remove maxSize to allow validation in handleFileChange for total size
    disabled: mode === "view" || submitting || uploading,
    multiple: true,
  });

  const {
    createMaintenanceSchedule,
    updateMaintenanceSchedule,
    loading: storeLoading,
  } = useEquipmentManagementStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const { rolesData, fetchAllRoles } = useRoleManageStore();
  const { equipmentSubcategory, fetchAllEquipmentSubcategory } =
    useEquipmentSubcategoryStore();
  const {
    equipmentManagement: equipmentManagementTypes,
    fetchAllEquipmentManagement,
  } = useEquipmentManagementMasterStore();
  const { maintenanceTypeList, fetchAllMaintenanceTypeList } =
    useMaintenanceTypeListStore();
  useEffect(() => {
    fetchOrgUsers();
    fetchAllRoles();
    fetchAllEquipmentManagement();
    fetchAllEquipmentSubcategory();
    fetchAllMaintenanceTypeList();
  }, [
    fetchOrgUsers,
    fetchAllRoles,
    fetchAllEquipmentManagement,
    fetchAllEquipmentSubcategory,
    fetchAllMaintenanceTypeList,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    if (!selectedEquipmentTypeId) {
      setEquipmentOptions([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setEquipmentOptionsLoading(true);
        const response = await fetchAllById(selectedEquipmentTypeId);
        if (cancelled) return;

        if (response.status === 200) {
          setEquipmentOptions(
            (response.data.message || []) as EquipmentInventory[],
          );
        } else {
          setEquipmentOptions([]);
          toast.error("Failed to fetch equipment data");
        }
      } catch (error: any) {
        if (cancelled) return;
        setEquipmentOptions([]);
        toast.error("Failed to fetch equipment data");
        console.error("Error fetching equipment data:", error);
      } finally {
        if (!cancelled) setEquipmentOptionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedEquipmentTypeId]);

  // Helpers to normalize ids that may come as string or {$oid}
  const extractId = (value: any): string =>
    typeof value === "string" ? value : value?.$oid || "";

  // Derive Maintenance Engineer role id and users
  const maintenanceEngineerRoleId = React.useMemo(() => {
    const role = rolesData?.find(
      (r: any) => r?.roleName === "Maintenance Engineer",
    );
    return extractId(role?._id);
  }, [rolesData]);

  const maintenanceEngineers = React.useMemo(() => {
    if (!orgUsersData?.length || !maintenanceEngineerRoleId) return [];
    return orgUsersData.filter(
      (u: any) => extractId(u?.role) === maintenanceEngineerRoleId,
    );
  }, [orgUsersData, maintenanceEngineerRoleId]);

  // Use maintenance tasks from store
  useEffect(() => {
    if (task && (mode === "view" || mode === "update" || mode === "complete")) {
      setFormData(task);

      // Show completion fields if in complete mode or task is completed
      setShowCompletionFields(
        mode === "complete" || task.status === "Completed",
      );

      // Set region and premises
      setSelectedRegion(task.region || "");
      setSelectedPremises(task.hospitalName || "");

      // Handle existing attachments
      if (task.attachments && task.attachments.length > 0) {
        const normalizedAttachments = task.attachments.map(
          (attachment: any) => {
            if (typeof attachment === "string") {
              return {
                url: attachment,
                filename: attachment.split("/").pop() || "Unknown file",
                uploadedAt: new Date().toISOString(),
              };
            } else {
              return {
                url: attachment.url || attachment,
                filename:
                  attachment.filename ||
                  attachment.name ||
                  attachment.originalName ||
                  attachment.url?.split("/").pop() ||
                  "Unknown file",
                uploadedAt: attachment.uploadedAt || new Date().toISOString(),
              };
            }
          },
        );
        setExistingAttachments(normalizedAttachments);

        const files = normalizedAttachments.map((attachment: any) => {
          const filename =
            attachment.filename ||
            attachment.name ||
            attachment.originalName ||
            attachment.url?.split("/").pop() ||
            "Unknown file";
          return {
            name: filename,
            url: attachment.url,
            type: getFileExtension(filename),
          };
        });
      }
    } else if (mode === "create") {
      // Reset form for new task
      setFormData({
        taskId: `MT-${new Date().getHours()}${new Date().getMinutes()}${new Date().getSeconds()}`,
        equipmentName: "",
        maintenanceType: "",
        scheduledDate: "",
        assignedTo: "",
        completionDate: "",
        status: "Scheduled",
        remarks: "",
        frequency: "",
        isVendorTask: false,
      });

      // Reset dynamic states
      setShowSchedulingDetails(true);
      setShowCompletionFields(false);
      setSelectedRegion("");
      setSelectedPremises("");
      setExistingAttachments([]);
      setFiles(null);
    }
  }, [task, mode]);

  // Dynamic form behavior effects
  useEffect(() => {
    // Update isVendorTask based on assignment type
    setFormData((prev) => ({
      ...prev,
      isVendorTask: assignmentType === "vendor",
    }));
  }, [assignmentType]);

  useEffect(() => {
    // Update region and premises in form data
    setFormData((prev) => ({
      ...prev,
      region: selectedRegion,
      hospitalName: selectedPremises,
    }));
  }, [selectedRegion, selectedPremises]);

  const handleInputChange = (
    field: keyof MaintenanceTask,
    value: string | number | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle frequency change
  const handleFrequencyChange = (frequency: string) => {
    setFormData((prev) => ({ ...prev, frequency: frequency as any }));

    // Auto-calculate next maintenance date
    if (formData.scheduledDate && frequency !== "One-time") {
      const scheduledDate = new Date(formData.scheduledDate);
      let nextDate = new Date(scheduledDate);

      switch (frequency) {
        case "Daily":
          nextDate.setDate(nextDate.getDate() + 1);
          break;
        case "Weekly":
          nextDate.setDate(nextDate.getDate() + 7);
          break;
        case "Monthly":
          nextDate.setMonth(nextDate.getMonth() + 1);
          break;
        case "Quarterly":
          nextDate.setMonth(nextDate.getMonth() + 3);
          break;
        case "Semi-Annual":
          nextDate.setMonth(nextDate.getMonth() + 6);
          break;
        case "Annual":
          nextDate.setFullYear(nextDate.getFullYear() + 1);
          break;
      }

      setFormData((prev) => ({
        ...prev,
        nextMaintenanceDate: nextDate.toISOString().split("T")[0],
      }));
    }
  };
  const handlePreviewFile = async (attachment: Attachment) => {
    const filename =
      attachment.filename ||
      attachment.name ||
      attachment.originalName ||
      attachment.url?.split("/").pop() ||
      "";

    if (canPreviewFile(filename)) {
      try {
        setIsLoading(true);

        const s3Key = attachment.url?.split("/").pop();
        if (!s3Key) {
          throw new Error("Invalid file URL");
        }

        const fileurl = await getsignedurl(s3Key, getFileExtension(filename));
        const filobj = {
          url: fileurl,
          name: filename,
          type: getFileExtension(filename),
        };
        setFileUrl(filobj);
        setIsDocumentViewerOpen(true);
      } catch (err: any) {
        console.error("Error previewing file:", err);
        setErrorMessage("File not found or failed to load");
        toast.error("Failed to preview file");
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.info(
        "Preview not available for this file type. Use download instead.",
      );
    }
  };
  const handleFilesChange = (newFiles: File[] | null) => {
    setFiles(newFiles);
  };
  const isDisabled = mode === "view" || submitting || uploading;
  // Handle assignment selection
  const handleAssignmentSelection = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      assignedTo: value,
    }));
  };

  const handleCloseDocumentViewer = () => {
    setIsDocumentViewerOpen(false);
    setFileUrl({ url: "", name: "", type: "" });
    setErrorMessage("");
  };

  const handleRemoveExistingAttachment = (index: number) => {
    if (mode === "view") return;

    setExistingAttachments((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "view") return;

    // Enhanced validation based on mode
    const requiredFields = ["equipmentName", "scheduledDate", "assignedTo"];

    if (mode === "complete") {
      requiredFields.push("completionDate");
    }

    const missingFields = requiredFields.filter(
      (field) => !formData[field as keyof MaintenanceTask],
    );

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in all required fields: ${missingFields.join(", ")}`,
      );
      return;
    }

    // Validate region and premises
    if (!selectedRegion || !selectedPremises) {
      toast.error("Please select both region and premises");
      return;
    }

    setSubmitting(true);
    setUploadErrorMessage("");

    // Validate total file size (10MB limit)
    if (files && files.length > 0) {
      const maxTotalSize = maxFileSize * 1024 * 1024;
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);

      if (totalSize > maxTotalSize) {
        toast.error(
          `Total file size exceeds the ${maxFileSize}MB limit. Please remove some files.`,
        );

        setSubmitting(false);
        return;
      }
    }

    let uploadedFileObjects: Array<{
      url: string;
      name: string;
      uploadedAt: string;
    }> = [];
    try {
      // Upload new files if any
      if (files && files.length > 0) {
        for (const file of files) {
          const s3Url = await uploadFileToS3(file);
          if (s3Url) {
            uploadedFileObjects.push({
              url: s3Url,
              name: file.name,
              uploadedAt: new Date().toISOString(),
            });
          } else {
            throw new Error(`Failed to upload file: ${file.name}`);
          }
        }
      }
    } catch (error) {
      setUploadErrorMessage(`File upload failed: ${(error as Error).message}`);
      setSubmitting(false);
      return;
    }

    try {
      // Prepare existing attachments as file objects
      const existingAttachmentObjects = existingAttachments.map((a) => ({
        url: a.url || (typeof a === "string" ? a : ""),
        name:
          a.filename ||
          a.name ||
          a.originalName ||
          a.url?.split("/").pop() ||
          "Unknown file",
        uploadedAt: a.uploadedAt || new Date().toISOString(),
      }));

      // Prepare the payload with all form data
      const payload = {
        ...formData,
        region: selectedRegion,
        hospitalName: selectedPremises,
        attachments: [...existingAttachmentObjects, ...uploadedFileObjects],
      };

      // Set status to completed if in complete mode
      if (mode === "complete") {
        payload.status = "Completed";
        payload.completionDate =
          payload.completionDate || new Date().toISOString().split("T")[0];
      }

      // Call the appropriate store function based on mode
      if (mode === "create") {
        const createPayload = {
          ...payload,
          taskId: payload.taskId || `MT-${Date.now()}`,
          attachments: payload.attachments || [],
        };
        await createMaintenanceSchedule(createPayload as any);
      } else if (mode === "update" && task?.id) {
        const updatePayload = {
          ...payload,
          attachments: payload.attachments || [],
        };
        await updateMaintenanceSchedule(task.id, updatePayload as any);
      } else if (mode === "complete" && task?.id) {
        const completePayload = {
          ...payload,
          attachments: payload.attachments || [],
        };
        await updateMaintenanceSchedule(task.id, completePayload as any);
      }

      onTaskComplete();
    } catch (error) {
      toast.error(`Failed to ${mode} maintenance task`);
    } finally {
      setSubmitting(false);
    }
  };

  const isReadOnly = mode === "view";
  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        modalClass="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        closeButton={false}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-[#61748e]">
              {mode === "create"
                ? "Create Maintenance Task"
                : mode === "update"
                  ? "Update Maintenance Task"
                  : mode === "complete"
                    ? "Complete Maintenance Task"
                    : "View Maintenance Task"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <LucideX size={24} className="text-gray-600" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            id="maintenance-task-form"
            className="space-y-6"
          >
            {/* Basic Information Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Task ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.taskId || ""}
                    disabled={userRole === "orguser"}
                    onChange={(e) =>
                      handleInputChange("taskId", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] bg-gray-100"
                    readOnly
                  />
                </div>

                {/* Equipment Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Equipment Type
                  </label>
                  <select
                    disabled={userRole === "orguser"}
                    value={selectedEquipmentTypeId}
                    onChange={(e) => {
                      setSelectedEquipmentTypeId(e.target.value);
                      setSelectedEquipmentSubcategoryId("");
                      handleInputChange("equipmentName", "");
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
                  >
                    <option value="">All Types</option>
                    {equipmentManagementTypes.map((t: any) => (
                      <option key={t._id} value={t._id}>
                        {t.equipmentName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Equipment Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Equipment Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    disabled={userRole === "orguser"}
                    value={formData.equipmentName || ""}
                    onChange={(e) =>
                      handleInputChange("equipmentName", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
                  >
                    <option value="">Select Equipment</option>
                    {(equipmentOptions || [])
                      .filter((e: EquipmentInventory) =>
                        selectedEquipmentSubcategoryId
                          ? extractId((e as any).subCategory) ===
                            selectedEquipmentSubcategoryId
                          : true,
                      )
                      .map((equipment: EquipmentInventory) => (
                        <option key={equipment._id} value={equipment._id}>
                          {equipment.equipmentName}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Region */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Region <span className="text-red-500">*</span>
                  </label>
                  <Dropdown
                    selectedValue={selectedRegion}
                    onChange={setSelectedRegion}
                    type="region"
                  />
                </div>

                {/* Premises */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Premises <span className="text-red-500">*</span>
                  </label>
                  <Dropdown
                    selectedValue={selectedPremises}
                    onChange={setSelectedPremises}
                    type="premises"
                    selectedRegion={selectedRegion}
                  />
                </div>
              </div>
            </div>

            {/* Task Type and Maintenance Details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Maintenance Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Task Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maintenance Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.maintenanceType}
                    onChange={(e) =>
                      handleInputChange("maintenanceType", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
                    disabled={isReadOnly || userRole === "orguser"}
                    required
                  >
                    <option value="">Select Maintenance Type</option>
                    {maintenanceTypeList.map((type: any) => (
                      <option key={type._id} value={type._id}>
                        {type.maintenanceTypeName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Scheduled Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scheduled Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.scheduledDate || ""}
                    onChange={(e) =>
                      handleInputChange("scheduledDate", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
                    readOnly={isReadOnly || userRole === "orguser"}
                    required
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.status || "Scheduled"}
                    onChange={(e) =>
                      handleInputChange("status", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
                    disabled={isReadOnly || mode === "create"}
                    required
                  >
                    <option value="Scheduled">Scheduled</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Overdue">Overdue</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Checklist <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.checkList || ""}
                    onChange={(e) =>
                      handleInputChange("checkList", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
                    // disabled={mode === "create"}
                    required
                  >
                    <option value="">Select Checklist</option>
                    {allChickList.map((checklist: any) => (
                      <option key={checklist._id} value={checklist._id}>
                        {checklist.checkList}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Frequency and Scheduling (Show for Preventive Maintenance) */}

            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Scheduling Configuration
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maintenance Frequency{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.frequency || "Monthly"}
                    onChange={(e) => handleFrequencyChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
                    disabled={isReadOnly || userRole === "orguser"}
                    required
                  >
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Semi-Annual">Semi-Annual</option>
                    <option value="Annual">Annual</option>
                    <option value="One-time">One-time</option>
                  </select>
                </div>

                {/* Next Maintenance Date (Auto-calculated) */}
                {formData.frequency !== "One-time" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Next Maintenance Date
                    </label>
                    <input
                      type="date"
                      value={formData.nextMaintenanceDate || ""}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Auto-calculated based on frequency
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Assignment Section */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Assignment Details
              </h3>

              {/* Assignment Type Selection */}
              {!isReadOnly && (
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Assign To <span className="text-red-500">*</span>
                  </label>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Dynamic Assignment Fields */}

                <div>
                  <select
                    value={formData.assignedTo || ""}
                    onChange={(e) => handleAssignmentSelection(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
                    disabled={isReadOnly || userRole === "orguser"}
                    required
                  >
                    <option value="">Select user</option>
                    {maintenanceEngineers.map((user: any) => {
                      const userId = extractId(user?._id);
                      const label =
                        `${user?.firstName || ""} ${
                          user?.lastName || ""
                        }`.trim() ||
                        user?.name ||
                        user?.email;
                      return (
                        <option key={userId} value={userId}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>

            {/* Completion Section (Show when completing task or viewing completed task) */}
            {(mode === "complete" ||
              showCompletionFields ||
              formData.status === "Completed") && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Completion Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Completion Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Completion Date{" "}
                      {mode === "complete" && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      type="date"
                      value={formData.completionDate || ""}
                      onChange={(e) =>
                        handleInputChange("completionDate", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
                      readOnly={mode === "view" || userRole === "orguser"}
                      required={mode === "complete"}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Remarks Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {mode === "complete" ? "Completion Notes" : "Remarks"}
              </label>
              <textarea
                value={formData.remarks || ""}
                onChange={(e) => handleInputChange("remarks", e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
                readOnly={
                  mode === "view" || userRole === "orguser" || isReadOnly
                }
                placeholder={
                  mode === "complete"
                    ? "Enter completion notes and observations..."
                    : "Enter any additional remarks or notes..."
                }
              />
            </div>
            <FileUpload
              mode={mode}
              files={files}
              onFilesChange={handleFilesChange}
              existingAttachments={existingAttachments}
              onRemoveExistingAttachment={handleRemoveExistingAttachment}
              onPreviewFile={handlePreviewFile}
              uploading={uploading}
              uploadProgress={uploadProgress}
              disabled={isDisabled}
              maxFileSizeMB={maxFileSize}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
            />

            {/* Action Buttons */}
          </form>
        </div>

        {/* Loading Overlay */}
        {(uploading || submitting || storeLoading) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
              <div className="flex justify-center mb-4">
                <svg
                  className="animate-spin h-8 w-8 text-teal-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <p className="text-gray-700 font-medium">
                {uploading
                  ? `Uploading files... ${uploadProgress}%`
                  : storeLoading
                    ? "Saving maintenance task..."
                    : "Processing your request..."}
              </p>
            </div>
          </div>
        )}
        {!isReadOnly && (
          <div className="flex justify-end gap-4 p-4 bg-[#5A7192]  sticky bottom-0">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-full bg-[#869bb7] text-white hover:bg-[#98afcf] focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="maintenance-task-form"
              disabled={submitting || uploading || storeLoading}
              className="px-6 py-2 bg-[#0fa9a6]  text-white rounded-full hover:bg-[#0e9693] focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting || uploading || storeLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {uploading ? `Uploading... ${uploadProgress}%` : "Saving..."}
                </>
              ) : (
                <>
                  {mode === "create" && "Create Task"}
                  {mode === "update" && "Update Task"}
                  {mode === "complete" && "Complete Task"}
                </>
              )}
            </button>
          </div>
        )}
        {/* Document Viewer Modal */}
        {isDocumentViewerOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-800">
                  Document Preview: {fileurl.name}
                </h3>
                <button
                  onClick={handleCloseDocumentViewer}
                  className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                  aria-label="Close document viewer"
                >
                  <XIcon size={20} />
                </button>
              </div>
              <div className="flex-1 p-4 overflow-auto">
                {errorMessage && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {errorMessage}
                  </div>
                )}
                {isLoading ? (
                  <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : fileurl.url ? (
                  fileurl.type === "pdf" ? (
                    <div className="w-full h-full">
                      <PdfViewer fileUrl={fileurl.url} />
                    </div>
                  ) : (
                    <div className="w-full h-full">
                      <DocumentViewer file={fileurl.url} name={fileurl.name} />
                    </div>
                  )
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <p>No document to display</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default CreateTaskModal;
